import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import request from "../../utils/http";
import toaster from "../../utils/toaster";
import { Spinner } from "baseui/spinner";
import ActionHeader from "../../components/ActionHeader";
import { Button } from "baseui/button";
import ActionBar from "../../components/ActionBar";
import { FieldGrid } from "./FieldGrid";
import { FormControl } from "baseui/form-control";
import { Select } from "baseui/select";
import { Textarea } from "baseui/textarea";
import { Checkbox } from "baseui/checkbox";
import Prose from "../../components/Prose";
import enums from "../../utils/enums";

const Container = styled.div``;

const CandidateCancel = () => {
	const navigate = useNavigate();
	const urlParams = useParams() as any;
	const [activeTab, setActiveTab] = React.useState<React.Key>(0);

	const [candidateId, setCandidateId] = useState("");

	const [data, setData] = useState(null as any);

	const [loaded, setLoaded] = useState(false);

	const [isCancelling, setIsCancelling] = useState(false);

	const updateUrlParams = async () => {
		setCandidateId(urlParams.candidateId);
	};

	const getCandidate = async () => {
		let result = await request(
			"GET",
			`/api/admin/candidate/${candidateId}`,
			null,
			true,
			false
		);
		if (result.success && result.data?.candidateId) {
			setData(result.data);

			setLoaded(true);
		} else {
			toaster.error("Unable to access candidate");
		}
	};

	useEffect(() => {
		if (candidateId != "") {
			getCandidate();
		}
	}, [candidateId]);

	useEffect(() => {
		updateUrlParams();
	}, [urlParams, updateUrlParams]);

	const [archivedReasonValue, setArchivedReasonValue] = useState<any>([]);
	const [note, setNote] = useState("");
	const [confirmIntent, setConfirmIntent] = useState(false);

	const doCancel = async () => {
		if (archivedReasonValue.length == 0) {
			toaster.error("You must provide a cancellation reason");
		}
		if (note.length == 0) {
			toaster.error("You must provide a cancellation note");
		}
		if (!confirmIntent) {
			toaster.error("You must confirm that you want to cancel this candidate");
		}

		if (confirmIntent && archivedReasonValue.length > 0 && note.length > 0) {
			if (!isCancelling) {
				setIsCancelling(true);

				let result = await request(
					"POST",
					`/api/admin/candidate-action/cancel-application/${candidateId}`,
					{
						cancellationReason: archivedReasonValue[0].value,
						cancellationNote: note,
					},
					true,
					true
				);

				navigate(`/candidate/${candidateId}`);
			}
		}
	};

	return (
		<Container>
			{!loaded && (
				<div>
					<Spinner />
				</div>
			)}
			{loaded && data && (
				<>
					<ActionHeader>
						<h1>
							Cancel Application for {data.fields["Candidate.FirstName"]}{" "}
							{data.fields["Candidate.LastName"]}
						</h1>
						<ActionBar>
							<Button
								kind="secondary"
								onClick={() => {
									navigate(`/candidate/${candidateId}`);
								}}
							>
								Go back
							</Button>
						</ActionBar>
					</ActionHeader>
					<hr />
					<Prose>
						<p>
							<strong>
								Are you sure you wish to cancel the application for{" "}
								{data.fields["Candidate.FirstName"]}{" "}
								{data.fields["Candidate.LastName"]}?
							</strong>
						</p>
						<p>
							Once this application is cancelled:
							<ul>
								<li>
									The candidate will no longer be able to log in to the portal
								</li>
								<li>
									The candidate will no longer appear on any dashboards (eg MOJ
									Check, Candidate lists)
								</li>
								<li>The candidate will move to the Archive screen</li>
							</ul>
						</p>
						<p>
							The candidate will not receive any emails automatically from this
							action - you will need to get in touch with the candidate to let
							them know.
						</p>
						<p>
							<em>
								(Note: This page can also be used to mark a candidate as "Hired"
								if their hiring process has stepped outside of the system / has
								skipped the process. If the candidate has been hired through the
								"Completion" workflow, this page is not required - the
								candidate's application will be automatically archived)
							</em>
						</p>
						<p>
							Please make a note to explain the reason for cancelling the
							application.
						</p>
					</Prose>
					<hr />
					<div>
						<FieldGrid>
							<div>
								<FormControl label={"Cancellation reason"}>
									<>
										<Select
											labelKey={"label"}
											valueKey={"value"}
											options={enums.candidateArchivedReason}
											maxDropdownHeight="12rem"
											clearable={false}
											value={archivedReasonValue}
											onChange={(params) => {
												setArchivedReasonValue(params.value);
											}}
										/>
									</>
								</FormControl>
							</div>
							<div>
								<FormControl label={"Cancellation note"}>
									<>
										<Textarea
											value={note}
											rows={3}
											onChange={(e: any) => {
												setNote(e.target.value);
											}}
										/>
									</>
								</FormControl>
							</div>
							<div>
								<FormControl label={"Confirm"}>
									<Checkbox
										checked={confirmIntent}
										onChange={(e: any) => setConfirmIntent(e.target.checked)}
									>
										Cancel {data.fields["Candidate.FirstName"]}{" "}
										{data.fields["Candidate.LastName"]}'s application
									</Checkbox>
								</FormControl>
							</div>
						</FieldGrid>
						<br />
						<br />
						<Button isLoading={isCancelling} onClick={doCancel}>
							Cancel Application
						</Button>
					</div>
				</>
			)}
		</Container>
	);
};
export default CandidateCancel;
